<script setup lang="ts">
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import PaypalIcon from '@/icons/Paypal.vue'
import PixIcon from '@/icons/PixIcon.vue'
import { cn } from '@/lib/utils'
import { FormSchema } from '@/pages/carts/show.vue'
import { Building2, CreditCard, ScanBarcode } from 'lucide-vue-next'
import { inject } from 'vue'
import { useI18n } from 'vue-i18n'
import CreditCardComponent from '../payment-methods/CreditCard.vue'
import Title from './Title.vue'

const { t } = useI18n()

const paymentMethods = [
    { value: 'pix', label: t('Pix'), icon: PixIcon },
    { value: 'credit_card', label: t('Credit Card (Issued in Brazil)'), icon: CreditCard, component: CreditCardComponent },
    { value: 'bank_slip', label: t('Bank slip'), icon: ScanBarcode },
    { value: 'paypal', label: t('Paypal (Issued in Brazil and Abroad)'), icon: PaypalIcon },
    { value: 'company_billing', label: t('CNPJ billing'), icon: Building2 },
]

const selectedValue = inject<string>('selectedPaymentMethod')

defineProps<{
    form: FormSchema
}>()
</script>

<template>
    <section>
        <Title :title="$t('Select payment method')" :description="$t('Please indicate the best payment method for you')" />

        <RadioGroup
            v-model="form.payment_type"
            class="mt-8"
            v-on:update:model-value="
                (value) => {
                    selectedValue = value
                }
            "
        >
            <div v-for="paymentMethod in paymentMethods" :key="paymentMethod.value">
                <Label
                    :class="
                        cn(
                            'flex items-center gap-x-4 rounded-[10px] border border-slate-300 p-6',
                            selectedValue === paymentMethod.value && 'border-primary',
                        )
                    "
                    :for="paymentMethod.value"
                >
                    <RadioGroupItem
                        :id="paymentMethod.value"
                        :value="paymentMethod.value"
                        class="data-[state=checked]:border-primary h-5 min-w-5 self-start"
                    />
                    <div class="w-full">
                        <div class="flex w-full flex-wrap justify-between gap-4">
                            <div class="flex items-center gap-4">
                                <component :is="paymentMethod.icon" class="h-5 w-5" />
                                <span class="whitespace-nowrap">{{ paymentMethod.label }}</span>
                            </div>
                        </div>
                    </div>
                </Label>

                <div v-if="selectedValue === paymentMethod.value" class="">
                    <component v-if="paymentMethod.component" :is="paymentMethod.component" class="mt-4" :form="form" />
                </div>
            </div>
        </RadioGroup>
    </section>
</template>
